import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar.v2'
import { useAppStore } from '@/stores/appStore'
import { cn } from '@/lib/utils'
import { getAppLayoutPresentation } from './appLayoutPresentation'
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  getBrowserAnalyticsConsent,
  shouldDeferProductOnboardingForAnalyticsConsent,
} from '@/lib/analyticsConsent'
import { isAnalyticsConfigured } from '@/lib/posthog'
import { canStartSidebarTour, REPORT_TOUR_COMPLETED_EVENT } from '@/features/tour/tourSteps'
import { SidebarTour } from '@/features/tour/SidebarTour'
import {
  obtenerOrganizaciones,
  resolveAutoSelectedOrganizationId,
} from '@/features/organizations/organizationPersistence'
import type { Plan } from '@/types'

interface AppLayoutProps {
  usuarioId?: string
  nombreUsuario?: string
  plan?: Plan
}

export function AppLayout({ usuarioId, nombreUsuario, plan }: AppLayoutProps) {
  const location = useLocation()
  const tamanoFuente = useAppStore(s => s.tamanoFuente)
  const organizacionActivaId = useAppStore(s => s.organizacionActivaId)
  const setOrganizacionActivaId = useAppStore(s => s.setOrganizacionActivaId)
  const [mostrarTourSidebar, setMostrarTourSidebar] = useState(false)
  const layout = getAppLayoutPresentation(location.pathname)

  useEffect(() => {
    if (!usuarioId) return
    let cancelado = false

    obtenerOrganizaciones()
      .then(organizations => {
        if (cancelado) return
        const nextOrganizationId = resolveAutoSelectedOrganizationId({
          currentOrganizationId: organizacionActivaId,
          organizations,
        })
        if (nextOrganizationId !== organizacionActivaId) {
          setOrganizacionActivaId(nextOrganizationId)
        }
      })
      .catch(() => undefined)

    return () => {
      cancelado = true
    }
  }, [organizacionActivaId, setOrganizacionActivaId, usuarioId])

  // Show sidebar tour only after the report tour has been completed.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined

    const scheduleSidebarTour = () => {
      if (!canStartSidebarTour() || timer) return
      if (shouldDeferProductOnboardingForAnalyticsConsent({
        analyticsConfigured: isAnalyticsConfigured(),
        consent: getBrowserAnalyticsConsent(),
      })) return
      timer = setTimeout(() => setMostrarTourSidebar(true), 600)
    }

    scheduleSidebarTour()
    window.addEventListener(REPORT_TOUR_COMPLETED_EVENT, scheduleSidebarTour)
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, scheduleSidebarTour)

    return () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener(REPORT_TOUR_COMPLETED_EVENT, scheduleSidebarTour)
      window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, scheduleSidebarTour)
    }
  }, [])

  const claseFuente = {
    sm: 'text-[13px]',
    md: 'text-[14px]',
    lg: 'text-[15px]',
  }[tamanoFuente]

  return (
    <div className={cn('flex h-screen bg-[#030712] overflow-hidden', claseFuente)}>
      {layout.showSidebar && <Sidebar nombreUsuario={nombreUsuario} plan={plan} />}

      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>

      {layout.showSidebarTour && mostrarTourSidebar && (
        <SidebarTour onClose={() => setMostrarTourSidebar(false)} />
      )}
    </div>
  )
}
